import { __decorate } from "tslib";
import { Component, Vue, Watch } from "vue-property-decorator";
import { max } from "lodash";
import { ConversationMasterBuilderService } from "@/services/conversation-master-builder";
import { fmtMsg, isGuid, ActionItem, GLUtil } from "commonui";
import { ConversationMasterBuilderLocale, CommonLocale } from "@/locales/localeid";
import { Table, Row, Col, Alert } from "ant-design-vue";
import { VersionGroupModule } from "@/modules/versiongroup";
import UnitState from "@/modules/unit";
import { BuilderPageType, CurriculumType, MethodologyTypeEnum, ParentType, QuickViewWindowType } from "@/util/enum";
import QuickViewWindow from "@/components/quick-view-window/quick-view-window.vue";
import { Paths } from "@/util/paths";
import { getModule } from "vuex-module-decorators";
import { Styles, Columns } from "./const";
import QuickViewDrawer from "./quick-view-drawer/quick-view-drawer.vue";
import { isAnyVersionSelected } from "@/util/func";
import PageTitle from "@/components/page-title/page-title.vue";
import ContentExposureConversation from "@/components/content-exposure/content-exposure-conversation/content-exposure-conversation.vue";
let ConversationMasterBuilder = class ConversationMasterBuilder extends Vue {
    constructor() {
        super(...arguments);
        this.versionGroupModule = VersionGroupModule;
        this.unitState = getModule(UnitState);
        this.columns = [];
        this.defaultColumns = [
            {
                title: fmtMsg(ConversationMasterBuilderLocale.ComponentColumn),
                dataIndex: Columns.Title,
                key: Columns.Title,
                width: 200,
                scopedSlots: { customRender: "title" }
            },
            {
                title: fmtMsg(ConversationMasterBuilderLocale.GeneralColumn),
                dataIndex: Columns.ConversationId,
                key: Columns.ConversationId,
                width: 80,
                align: "center"
            }
        ];
        this.unit = {
            id: -1,
            unit: 0,
            displayName: "",
            componentCount: 0,
            sequence: 0,
            disable: false,
            curriculumType: CurriculumType.GrapeSEED
        };
        this.dataSource = [];
        this.loading = false;
        this.service = ConversationMasterBuilderService;
        this.quickViewDrawerProps = {};
        this.quickViewProps = { quickViewType: QuickViewWindowType.Content };
        this.noVersionGroup = false;
        this.exposureData = null;
    }
    mounted() {
        this.versionGroupModule.setDisabled(false);
        this.setColumns();
        if (isAnyVersionSelected()) {
            this.getMasterData();
        }
        else {
            this.noVersionGroup = true;
        }
    }
    destroyed() {
        this.versionGroupModule.setDisabled(true);
    }
    get quickViewWindowType() {
        return QuickViewWindowType;
    }
    getMasterData() {
        this.loading = true;
        const unitId = this.getValidatedUnitId(parseInt(this.$route.params.unitId));
        this.unit = this.unitState.units.find((item) => item.id === unitId);
        const { lessonPlanVersionId } = this.$route.params;
        this.service
            .getMasterData(unitId, this.selectedVersionGroup, this.curriculumType, lessonPlanVersionId)
            .then((response) => {
            this.dataSource = response;
            this.loading = false;
            this.setColumns(response);
        })
            .catch(() => {
            this.loading = false;
        });
    }
    setColumns(data) {
        if (data && data.length) {
            const maxColCount = max(data.map((item) => item.exposureConversations.length));
            const [title, general] = this.defaultColumns;
            general.customRender = this.generalCustomRender;
            general.customCell = this.generalCustomCell;
            this.columns = [
                { ...title },
                {
                    ...general,
                    customRender: this.generalCustomRender,
                    customCell: this.generalCustomCell
                },
                ...[...Array(maxColCount).keys()].map((key) => ({
                    title: key + 1,
                    dataIndex: `exposureConversations[${key}]`,
                    key: key + 1,
                    width: 40,
                    align: "center",
                    customRender: this.exposureCustomRender,
                    customCell: (record) => this.exposureCustomCell(record, key)
                }))
            ];
        }
        else {
            this.columns = this.defaultColumns;
        }
    }
    generalCustomRender(text, record) {
        const { parentTypeId } = record;
        /*
            If conversation exists for content -> green.
            If conversation does not exist for content -> red.
            If content is parent, then component conversation is not accessible, show red.
        */
        const style = text && parentTypeId !== ParentType.Parent ? Styles.BackgroundColorGreen : Styles.BackgroundColorRed;
        return {
            children: <div></div>,
            attrs: {
                style: style + Styles.CursorPointer
            }
        };
    }
    generalCustomCell(record) {
        return {
            on: {
                click: () => {
                    const { componentId, conversationId, title, parentTypeId } = record;
                    const [add, edit] = this.commonAddEditText;
                    this.quickViewDrawerProps = {
                        placement: "bottom",
                        title: fmtMsg(CommonLocale.CommonQuickViewTitle),
                        visible: true,
                        close: this.onDrawerClose
                    };
                    const getText = (parentTypeId, conversationId) => {
                        // Add/Edit link should be present for a parent component.
                        if (parentTypeId === ParentType.Parent) {
                            return null;
                        }
                        return conversationId ? edit : add;
                    };
                    this.quickViewProps = {
                        id: componentId,
                        quickViewType: QuickViewWindowType.Content,
                        extra: {
                            actions: [
                                {
                                    text: getText(parentTypeId, conversationId) || "",
                                    tooltip: conversationId ? edit : add,
                                    route: GLUtil.pathStringify(Paths.ContentConversationBuilder, {
                                        pagetype: BuilderPageType.ContentInfo,
                                        id: componentId
                                    })
                                }
                            ]
                        },
                        showContentText: parentTypeId !== ParentType.Parent // hide content text for parent component.
                    };
                }
            }
        };
    }
    exposureCustomRender(text, record) {
        let style = "";
        /*
            If a complete conversation exists for exposure -> green.
            If conversation does not exist or is incomplete for exposure -> red.
            If exposure does not exists -> grey.
        */
        if (text && text.exposureId) {
            style = text.conversationId && text.isComplete ? Styles.BackgroundColorGreen : Styles.BackgroundColorRed;
            style = style + Styles.CursorPointer;
        }
        else {
            style = Styles.BackgroundColorGrey;
        }
        return {
            children: <div></div>,
            attrs: {
                style
            }
        };
    }
    exposureCustomCell(record, key) {
        return {
            on: {
                click: () => {
                    const expConv = record.exposureConversations[key];
                    if (expConv && expConv.exposureId) {
                        const [add, edit] = this.commonAddEditText;
                        this.quickViewDrawerProps = {
                            placement: "bottom",
                            title: fmtMsg(CommonLocale.CommonQuickViewTitle),
                            visible: true,
                            close: this.onDrawerClose
                        };
                        this.exposureData = {
                            methodologies: [MethodologyTypeEnum.Independent],
                            contentId: expConv.componentId,
                            id: expConv?.exposureId,
                            conversationId: expConv.conversationId
                        };
                        this.quickViewProps = {
                            id: expConv?.exposureId,
                            quickViewType: QuickViewWindowType.Exposure,
                            isExposureSlotAdded: false,
                            extra: {
                                title: `${record.title} - ${fmtMsg(ConversationMasterBuilderLocale.ExposureText)} ${key + 1}`,
                                actions: [
                                // {
                                // 	text: expConv && expConv.conversationId ? edit : add,
                                // 	tooltip: expConv && expConv.conversationId ? edit : add,
                                // 	route: GLUtil.pathStringify(Paths.ExposureConversationBuilder, {
                                // 		contentId: expConv.componentId,
                                // 		pagetype: BuilderPageType.ExposureInfo,
                                // 		id: expConv.exposureId
                                // 	})
                                // }
                                ]
                            }
                        };
                    }
                }
            }
        };
    }
    onBack() {
        const { methodology, lessonPlanVersionId } = this.$route.params;
        const path = GLUtil.pathStringify(Paths.LessonPlanListWithParams, {
            versionGroupId: this.selectedVersionGroup,
            methodology,
            lessonPlanVersionId
        });
        const unitId = this.getValidatedUnitId(parseInt(this.$route.params.unitId));
        const query = GLUtil.queryStringify({ unitId });
        this.$router.push({ path: `${path}?${query}` });
    }
    onDrawerClose(e) {
        this.quickViewDrawerProps = {
            ...this.quickViewDrawerProps,
            visible: false
        };
    }
    onVersionGroupChanged(value) {
        if (isAnyVersionSelected()) {
            this.getMasterData();
            this.noVersionGroup = false;
        }
        else {
            this.noVersionGroup = true;
        }
    }
    getValidatedUnitId(unitId) {
        const units = this.unitState.units;
        const unit = units.find((item) => item.id == unitId);
        if (!unit) {
            return -1;
        }
        if (unit.curriculumType === this.curriculumType) {
            return unitId;
        }
        return units.some((item) => item.curriculumType === this.curriculumType)
            ? units.filter((item) => item.curriculumType === this.curriculumType)[0].id
            : -1;
    }
    localize(key) {
        return fmtMsg(key);
    }
    get commonLocale() {
        return CommonLocale;
    }
    get tableColumns() {
        return Columns;
    }
    get curriculumType() {
        if (isGuid(this.versionGroupModule.selectedGsVersion)) {
            return CurriculumType.GrapeSEED;
        }
        if (isGuid(this.versionGroupModule.selectedLsVersion)) {
            return CurriculumType.LittleSEED;
        }
        return -1;
    }
    get selectedVersionGroup() {
        if (isGuid(this.versionGroupModule.selectedGsVersion)) {
            return this.versionGroupModule.selectedGsVersion;
        }
        if (isGuid(this.versionGroupModule.selectedLsVersion)) {
            return this.versionGroupModule.selectedLsVersion;
        }
        return "-1";
    }
    get commonAddEditText() {
        return [fmtMsg(CommonLocale.CommonAddButtonText), fmtMsg(CommonLocale.CommonEditButtonText)];
    }
};
__decorate([
    Watch("versionGroupModule.selectedGsVersion"),
    Watch("versionGroupModule.selectedLsVersion")
], ConversationMasterBuilder.prototype, "onVersionGroupChanged", null);
ConversationMasterBuilder = __decorate([
    Component({
        components: {
            Table,
            Row,
            Col,
            Alert,
            QuickViewWindow,
            QuickViewDrawer,
            ActionItem,
            PageTitle,
            ContentExposureConversation
        }
    })
], ConversationMasterBuilder);
export default ConversationMasterBuilder;
